import { useCallback, useState } from "react";

export const useLocalStorage: {
  <T extends unknown>(
    key: string,
  ): [T | undefined, (value?: T | ((val?: T) => T)) => void];
  <T extends unknown>(
    key: string,
    initialValue: T,
  ): [T, (value: T | ((val: T) => T)) => void];
} = <T>(key: string, initialValue?: T) => {
  const [value, setValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? (JSON.parse(item) as T) : initialValue;
    } catch (error) {
      return initialValue;
    }
  });

  const update = useCallback(
    (newValue?: T | ((prev?: T) => T)) => {
      const valueToStore =
        newValue instanceof Function ? newValue(value) : newValue;

      setValue(valueToStore);

      if (valueToStore === undefined) {
        window.localStorage.removeItem(key);
      } else {
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
      }
    },
    [key, value],
  );

  return [value, update] as const;
};
